import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ShoppingBag, Truck, Loader2, CheckCircle } from 'lucide-react';

const CheckoutPage = () => {
  const { items, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [address, setAddress] = useState({
    name: '', phone: '', address: '', city: '', state: '', pincode: '',
  });

  // Fetch user profile data when component mounts
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, phone, address, city, state, postal_code')
          .eq('user_id', user.id)
          .single();

        if (data) {
          setAddress({
            name: data.full_name || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            pincode: data.postal_code || ''
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground mb-4">Please sign in to checkout.</p>
        <Button onClick={() => navigate('/auth')}>Sign In</Button>
      </div>
    );
  }

  if (items.length === 0 && !orderPlaced) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <p className="text-muted-foreground mb-4">Your cart is empty</p>
        <Button onClick={() => navigate('/products')}>Browse Products</Button>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold mb-2">Order Confirmed!</h1>
        <p className="text-muted-foreground mb-6">Thank you! Your order has been placed. Pay cash on delivery.</p>
        <Button onClick={() => navigate('/')}>Continue Shopping</Button>
      </div>
    );
  }

  const isFormValid = Object.values(address).every(v => v.trim().length > 0);

  const handlePlaceOrder = async () => {
    if (!isFormValid) {
      toast.error('Please fill in all address fields');
      return;
    }
    if (totalPrice < 1000) {
      toast.error('Minimum order amount is ₹1,000');
      return;
    }

    setLoading(true);
    try {
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          total: totalPrice,
          payment_method: 'cod',
          payment_status: 'cod',
          status: 'pending',
          phone: address.phone,
          shipping_address: address,
        })
        .select()
        .single();

      if (orderError) throw orderError;

      const orderItems = items.map(i => ({
        order_id: order.id,
        product_id: i.product.id,
        product_name: i.product.name,
        price: i.product.price,
        quantity: i.quantity,
      }));

      const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
      if (itemsError) throw itemsError;

      setOrderPlaced(true);
      clearCart();
      toast.success('Order placed! Pay on delivery.');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Checkout | Debnath Telecom</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="font-heading text-2xl font-bold mb-6">Checkout</h1>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-3 space-y-4">
            <h2 className="font-heading text-lg font-semibold">Shipping Address</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" value={address.name} onChange={e => setAddress(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={address.phone} onChange={e => setAddress(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input id="address" value={address.address} onChange={e => setAddress(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="pincode">Pincode</Label>
                <Input id="pincode" value={address.pincode} onChange={e => setAddress(p => ({ ...p, pincode: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="md:col-span-2 bg-muted/50 p-5 rounded-lg h-fit space-y-4">
            <h2 className="font-heading text-lg font-semibold">Order Summary</h2>
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.product.id} className="flex justify-between text-sm">
                  <span className="truncate mr-2">{item.product.name} ×{item.quantity}</span>
                  <span className="font-medium whitespace-nowrap">₹{(item.product.price * item.quantity).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span>₹{totalPrice.toLocaleString('en-IN')}</span>
            </div>

            <Button
              className="w-full gap-2"
              size="lg"
              disabled={loading || !isFormValid}
              onClick={handlePlaceOrder}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
              {loading ? 'Placing Order...' : 'Place Order (Cash on Delivery)'}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Pay with cash when your order is delivered.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;
